import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { useEffect, useRef, useState, createContext, useContext } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { authClient } from "../../lib/auth-client.ts";
import { ProfileDialogProvider } from "@/components/profile-dialog/profile-dialog-provider";
import type { RoleKind } from "@/components/vroom/role-pill";
import { wipeLocalState } from "@/lib/sign-out";
import { RealtimeClient } from "../../lib/realtime.ts";

interface AuthedUser {
  name: string;
  email: string;
  role?: RoleKind;
}

const RealtimeContext = createContext<RealtimeClient | null>(null);
export function useRealtime(): RealtimeClient | null {
  return useContext(RealtimeContext);
}

const SESSION_STALE_MS = 5 * 60 * 1000;

export const Route = createFileRoute("/_authed")({
  staleTime: SESSION_STALE_MS,
  beforeLoad: async ({ context, location }) => {
    const { queryClient } = context as { queryClient: QueryClient };
    const session = await queryClient.ensureQueryData({
      queryKey: ["session"],
      queryFn: async () => {
        const { data } = await authClient.getSession();
        return data ?? null;
      },
      staleTime: SESSION_STALE_MS,
    });
    if (!session?.user) {
      throw redirect({ to: "/sign-in" });
    }
    if (!session.user.twoFactorEnabled && !location.pathname.startsWith("/onboarding/mfa")) {
      throw redirect({ to: "/onboarding/mfa" });
    }
    const knownUserId = (() => {
      try {
        return sessionStorage.getItem("vroom.lastUserId");
      } catch {
        return null;
      }
    })();
    if (knownUserId && knownUserId !== session.user.id) {
      await wipeLocalState({ userId: knownUserId });
      // No nav — let normal load continue with fresh state for the new user.
    }
    try {
      sessionStorage.setItem("vroom.lastUserId", session.user.id);
    } catch {
      /* ignore */
    }

    Sentry.setUser({
      id: session.user.id,
      role: (session.user as { role?: string }).role,
    });
    return { user: session.user };
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  const ctx = Route.useRouteContext() as { user: AuthedUser & { id: string } };
  const queryClient = useQueryClient();
  const [client, setClient] = useState<RealtimeClient | null>(null);
  const clientRef = useRef<RealtimeClient | null>(null);

  useEffect(() => {
    const wsUrl =
      (typeof window !== "undefined" && window.location.protocol === "https:"
        ? "wss://"
        : "ws://") +
      (typeof window !== "undefined" ? window.location.host : "") +
      "/api/v1/ws";
    const rt = new RealtimeClient(wsUrl);
    rt.connect(queryClient, ctx.user.id);
    clientRef.current = rt;
    setClient(rt);
    return () => {
      rt.disconnect();
      clientRef.current = null;
      setClient(null);
    };
  }, [queryClient, ctx.user.id]);

  return (
    <RealtimeContext.Provider value={client}>
      <ProfileDialogProvider user={ctx.user}>
        <Outlet />
      </ProfileDialogProvider>
    </RealtimeContext.Provider>
  );
}
